import { create } from 'zustand';

/**
 * Auth-state store backed by localStorage.
 *
 * Persists the JWT access token under `roundrecon.jwt` and the
 * refresh token under `roundrecon.refresh`. Server-side enforcement is
 * still the source of truth; the client-decoded claims are only used
 * for UI gating (show/hide nav, redirect on missing token).
 *
 * Public surface:
 *   - `setToken(token)` — drop just the access token (kept for the
 *     existing admin-token-paste flow).
 *   - `signIn(bundle)` — persist both tokens after a register/login
 *     round-trip. This is what the new auth pages call.
 *   - `signOut()` — clear both tokens (paired with `POST /api/auth/logout`
 *     from the caller).
 *   - `logout()` — alias for `signOut()` retained for the admin pages.
 *   - `hydrate()` — re-read from localStorage; used by tests and after
 *     manual localStorage writes during dev.
 */

const STORAGE_KEY = 'roundrecon.jwt';
const REFRESH_STORAGE_KEY = 'roundrecon.refresh';

export interface JwtClaims {
  sub: string;
  email?: string;
  role?: string;
  /** Email-verification state (#209). Informational — never gates auth. */
  emailVerified?: boolean;
  exp?: number;
  iat?: number;
}

export interface AuthSignInBundle {
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
  user?: { id: string; email: string; role: string; emailVerified?: boolean };
}

export interface AuthState {
  token: string | null;
  refreshToken: string | null;
  claims: JwtClaims | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  /** Whether the signed-in user's email is verified (#209). False when
   * unauthenticated or when the claim is absent/falsey. */
  emailVerified: boolean;
  setToken: (token: string | null) => void;
  signIn: (bundle: AuthSignInBundle) => void;
  signOut: () => void;
  logout: () => void;
  hydrate: () => void;
}

function decodeJwtClaims(token: string): JwtClaims | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json =
      typeof atob === 'function' ? atob(padded) : Buffer.from(padded, 'base64').toString('utf-8');
    return JSON.parse(json) as JwtClaims;
  } catch {
    return null;
  }
}

function isExpired(claims: JwtClaims | null): boolean {
  if (!claims?.exp) return false;
  return claims.exp * 1000 < Date.now();
}

function readStored(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* ignore storage errors (private mode, etc.) */
  }
}

interface DerivedAuth {
  token: string | null;
  refreshToken: string | null;
  claims: JwtClaims | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  emailVerified: boolean;
}

function deriveState(token: string | null, refreshToken: string | null): DerivedAuth {
  if (!token) {
    return {
      token: null,
      refreshToken: refreshToken ?? null,
      claims: null,
      isAuthenticated: false,
      isAdmin: false,
      emailVerified: false,
    };
  }
  const claims = decodeJwtClaims(token);
  if (!claims || isExpired(claims)) {
    writeStored(STORAGE_KEY, null);
    return {
      token: null,
      refreshToken,
      claims: null,
      isAuthenticated: false,
      isAdmin: false,
      emailVerified: false,
    };
  }
  return {
    token,
    refreshToken,
    claims,
    isAuthenticated: true,
    isAdmin: claims.role === 'admin',
    emailVerified: claims.emailVerified === true,
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  ...deriveState(readStored(STORAGE_KEY), readStored(REFRESH_STORAGE_KEY)),
  setToken(token) {
    writeStored(STORAGE_KEY, token);
    set((state) => deriveState(token, state.refreshToken));
  },
  signIn(bundle) {
    writeStored(STORAGE_KEY, bundle.accessToken);
    writeStored(REFRESH_STORAGE_KEY, bundle.refreshToken);
    set(deriveState(bundle.accessToken, bundle.refreshToken));
  },
  signOut() {
    writeStored(STORAGE_KEY, null);
    writeStored(REFRESH_STORAGE_KEY, null);
    set(deriveState(null, null));
  },
  logout() {
    writeStored(STORAGE_KEY, null);
    writeStored(REFRESH_STORAGE_KEY, null);
    set(deriveState(null, null));
  },
  hydrate() {
    set(deriveState(readStored(STORAGE_KEY), readStored(REFRESH_STORAGE_KEY)));
  },
}));

/** React hook for admin-only views; returns the same shape as the store. */
export function useAdminAuth(): AuthState {
  return useAuthStore();
}

/** Test/utility: expose the JWT storage keys for fixtures. */
export const AUTH_STORAGE_KEY = STORAGE_KEY;
export const REFRESH_STORAGE_KEY_NAME = REFRESH_STORAGE_KEY;
